/**
 * NeuerKontaktDialog — Professioneller Multi-Step Dialog zum Erstellen neuer Kontakte
 * Mobile-optimiert: Fullscreen auf Smartphones, zentriert auf Desktop
 * HubSpot-Style mit Firmenzuordnung und Tags
 */

import { useState, useEffect, useRef } from 'react';
import {
  X, User, Building2, Mail, Phone, Smartphone, MapPin,
  Globe, Linkedin, Tag, Star, ChevronRight, ChevronLeft,
  Check, Plus, Search, Briefcase, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import type { CRMKontakt, CRMFirma } from '@/hooks/useCRM';
import LabelPicker from '@/components/kontakte/LabelPicker';
import BetriebePicker from '@/components/kontakte/BetriebePicker';
import type { KontaktLabel, BetriebSchluessel } from '@/lib/kontakt-labels';

interface NeuerKontaktDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (kontakt: Partial<CRMKontakt>) => Promise<any>;
  firmen?: CRMFirma[];
  initialEmail?: string;
}

const KATEGORIEN = [
  { value: 'kunde', label: 'Kunde', icon: User },
  { value: 'partner', label: 'Partner', icon: Building2 },
  { value: 'werkstatt', label: 'Werkstatt', icon: Briefcase },
  { value: 'versicherung', label: 'Versicherung', icon: Building2 },
  { value: 'anwalt', label: 'Anwalt', icon: Briefcase },
  { value: 'mietwagen', label: 'Mietwagen', icon: Briefcase },
  { value: 'autoglas', label: 'Autoglas', icon: Briefcase },
  { value: 'sonstige', label: 'Sonstige', icon: Tag },
];

const STATUS_OPTIONS = [
  { value: 'lead', label: 'Lead' },
  { value: 'aktiv', label: 'Aktiv' },
  { value: 'kunde', label: 'Kunde' },
  { value: 'inaktiv', label: 'Inaktiv' },
];

const TAGS_VORSCHLAEGE = [
  'VIP', 'Stammkunde', 'Neukunde', 'Privat', 'Gewerblich',
  'Versicherung', 'Werkstatt', 'Anwalt', 'Mietwagen', 'Autoglas',
  'Gutachten', 'Unfallgiganten', 'Empfehlung', 'Newsletter',
];

export default function NeuerKontaktDialog({ open, onClose, onCreate, firmen = [], initialEmail }: NeuerKontaktDialogProps) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);

  // Step 1: Grunddaten
  const [name, setName] = useState('');
  const [email, setEmail] = useState(initialEmail || '');
  const [telefon, setTelefon] = useState('');
  const [mobil, setMobil] = useState('');
  const [kategorie, setKategorie] = useState('kunde');

  // Step 2: Firma & Position
  const [firma, setFirma] = useState('');
  const [position, setPosition] = useState('');
  const [firmaSearch, setFirmaSearch] = useState('');
  const [selectedFirmenIds, setSelectedFirmenIds] = useState<string[]>([]);

  // Step 3: Adresse & Details
  const [adresse, setAdresse] = useState('');
  const [plz, setPlz] = useState('');
  const [ort, setOrt] = useState('');
  const [webseite, setWebseite] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [geburtstag, setGeburtstag] = useState('');
  const [status, setStatus] = useState('lead');
  const [tags, setTags] = useState<string[]>([]);
  const [notizen, setNotizen] = useState('');
  const [tagInput, setTagInput] = useState('');
  // Labels & Betriebe (Issue #573)
  const [selectedLabels, setSelectedLabels] = useState<KontaktLabel[]>([]);
  const [selectedBetriebe, setSelectedBetriebe] = useState<BetriebSchluessel[]>([]);
  const [primaererBetrieb, setPrimaererBetrieb] = useState<BetriebSchluessel | null>(null);

  // Reset bei Öffnen
  useEffect(() => {
    if (open) {
      setStep(1);
      setName(''); setEmail(initialEmail || ''); setTelefon(''); setMobil('');
      setKategorie('kunde'); setFirma(''); setPosition('');
      setFirmaSearch(''); setSelectedFirmenIds([]);
      setAdresse(''); setPlz(''); setOrt('');
      setWebseite(''); setLinkedin(''); setGeburtstag('');
      setStatus('lead'); setTags([]); setNotizen(''); setTagInput('');
      setSelectedLabels([]); setSelectedBetriebe([]); setPrimaererBetrieb(null);
    }
  }, [open, initialEmail]);

  // Body scroll verhindern
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [open]);

  const filteredFirmen = firmen.filter(f =>
    !firmaSearch || f.name.toLowerCase().includes(firmaSearch.toLowerCase())
  );

  const toggleTag = (tag: string) => {
    setTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const addCustomTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags(prev => [...prev, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Bitte einen Namen eingeben');
      setStep(1);
      return;
    }
    setSaving(true);
    try {
      const created = await onCreate({
        name: name.trim(),
        email: email.trim() || undefined,
        telefon: telefon.trim() || undefined,
        mobil: mobil.trim() || undefined,
        kategorie,
        firma: firma.trim() || undefined,
        position: position.trim() || undefined,
        firmen_ids: selectedFirmenIds.length > 0 ? selectedFirmenIds : undefined,
        adresse: adresse.trim() || undefined,
        plz: plz.trim() || undefined,
        ort: ort.trim() || undefined,
        webseite: webseite.trim() || undefined,
        linkedin: linkedin.trim() || undefined,
        geburtstag: geburtstag || undefined,
        status,
        tags,
        notizen: notizen.trim() || undefined,
        favorit: false,
      });

      // Persist labels and betriebe after contact creation
      if (created?.id) {
        const baseOpts = { method: 'POST', headers: { 'Content-Type': 'application/json' } };
        if (selectedLabels.length > 0) {
          const labelsRes = await fetch('/api/kontakt-labels', {
            ...baseOpts,
            body: JSON.stringify({ action: 'setLabels', kontakt_id: created.id, labels: selectedLabels }),
          });
          if (!labelsRes.ok) {
            toast.error('Labels konnten nicht gespeichert werden');
          }
        }
        if (selectedBetriebe.length > 0) {
          const betriebeRes = await fetch('/api/kontakt-labels', {
            ...baseOpts,
            body: JSON.stringify({
              action: 'setBetriebe',
              kontakt_id: created.id,
              betriebe: selectedBetriebe,
              primaerer_betrieb: primaererBetrieb,
            }),
          });
          if (!betriebeRes.ok) {
            toast.error('Betriebs-Zuordnung konnte nicht gespeichert werden');
          }
        }
      }

      toast.success('Kontakt erstellt', { description: name.trim() });
      onClose();
    } catch (err: any) {
      toast.error('Fehler: ' + (err.message || 'Kontakt konnte nicht erstellt werden'));
    }
    setSaving(false);
  };

  if (!open) return null;

  const totalSteps = 3;

  return (
    <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} />

      {/* Dialog */}
      <div className="relative w-full sm:max-w-lg bg-white sm:rounded-2xl shadow-2xl flex flex-col
        max-h-[100dvh] sm:max-h-[85vh] h-full sm:h-auto">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#1B3A5C] flex items-center justify-center">
              <User className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Neuer Kontakt</h2>
              <p className="text-xs text-gray-400">Schritt {step} von {totalSteps}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-5 pt-3 flex-shrink-0">
          <div className="flex gap-1.5">
            {[1, 2, 3].map(s => (
              <div key={s} className={`h-1 flex-1 rounded-full transition-colors ${s <= step ? 'bg-[#1B3A5C]' : 'bg-gray-100'}`} />
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-2 font-medium">
            {step === 1 && 'Grunddaten'}
            {step === 2 && 'Firma & Zuordnung'}
            {step === 3 && 'Details & Tags'}
          </p>
        </div>

        {/* Content — scrollbar */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* Step 1: Grunddaten */}
          {step === 1 && (
            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Name *</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input value={name} onChange={e => setName(e.target.value)}
                    placeholder="Vor- und Nachname"
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:bg-white focus:border-[#1B3A5C]/40 focus:outline-none transition-colors"
                    autoFocus />
                </div>
              </div>

              {/* E-Mail */}
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">E-Mail</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="email@beispiel.de" type="email"
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:bg-white focus:border-[#1B3A5C]/40 focus:outline-none transition-colors" />
                </div>
              </div>

              {/* Telefon + Mobil */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Telefon</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input value={telefon} onChange={e => setTelefon(e.target.value)}
                      placeholder="Festnetz" type="tel"
                      className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:bg-white focus:border-[#1B3A5C]/40 focus:outline-none transition-colors" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Mobil</label>
                  <div className="relative">
                    <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input value={mobil} onChange={e => setMobil(e.target.value)}
                      placeholder="Mobil" type="tel"
                      className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:bg-white focus:border-[#1B3A5C]/40 focus:outline-none transition-colors" />
                  </div>
                </div>
              </div>

              {/* Kategorie */}
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Kategorie</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {KATEGORIEN.map(k => (
                    <button key={k.value} onClick={() => setKategorie(k.value)}
                      className={`flex flex-col items-center gap-1 p-2.5 rounded-xl border text-xs font-medium transition-all
                        ${kategorie === k.value
                          ? 'border-[#1B3A5C] bg-[#1B3A5C]/5 text-[#1B3A5C]'
                          : 'border-gray-200 bg-gray-50 text-gray-500 hover:border-gray-300'}`}>
                      <k.icon className="w-4 h-4" />
                      <span className="truncate text-[10px]">{k.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Firma & Zuordnung */}
          {step === 2 && (
            <div className="space-y-4">
              {/* Firma (Freitext) */}
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Firmenname</label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input value={firma} onChange={e => setFirma(e.target.value)}
                    placeholder="z.B. Autohaus Müller GmbH"
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:bg-white focus:border-[#1B3A5C]/40 focus:outline-none transition-colors" />
                </div>
              </div>

              {/* Position */}
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Position / Rolle</label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input value={position} onChange={e => setPosition(e.target.value)}
                    placeholder="z.B. Geschäftsführer, Werkstattleiter"
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:bg-white focus:border-[#1B3A5C]/40 focus:outline-none transition-colors" />
                </div>
              </div>

              {/* Bestehende Firmen zuordnen */}
              {firmen.length > 0 && (
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Firma(en) zuordnen</label>
                  <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input value={firmaSearch} onChange={e => setFirmaSearch(e.target.value)}
                      placeholder="Firma suchen..."
                      className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:bg-white focus:border-[#1B3A5C]/40 focus:outline-none transition-colors" />
                  </div>
                  <div className="max-h-36 overflow-y-auto space-y-1 rounded-xl border border-gray-100 p-2">
                    {filteredFirmen.map(f => (
                      <button key={f.id}
                        onClick={() => setSelectedFirmenIds(prev =>
                          prev.includes(f.id) ? prev.filter(id => id !== f.id) : [...prev, f.id]
                        )}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm transition-colors
                          ${selectedFirmenIds.includes(f.id)
                            ? 'bg-[#1B3A5C]/5 text-[#1B3A5C] font-medium'
                            : 'hover:bg-gray-50 text-gray-600'}`}>
                        <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0
                          ${selectedFirmenIds.includes(f.id) ? 'bg-[#1B3A5C] border-[#1B3A5C]' : 'border-gray-300'}`}>
                          {selectedFirmenIds.includes(f.id) && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="truncate">{f.name}</p>
                          {f.kategorie && <p className="text-[10px] text-gray-400">{f.kategorie}</p>}
                        </div>
                      </button>
                    ))}
                    {filteredFirmen.length === 0 && (
                      <p className="text-xs text-gray-400 text-center py-3">Keine Firmen gefunden</p>
                    )}
                  </div>
                </div>
              )}

              {/* Status */}
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Status</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {STATUS_OPTIONS.map(s => (
                    <button key={s.value} onClick={() => setStatus(s.value)}
                      className={`py-2 rounded-xl border text-xs font-medium transition-all
                        ${status === s.value
                          ? 'border-[#1B3A5C] bg-[#1B3A5C]/5 text-[#1B3A5C]'
                          : 'border-gray-200 bg-gray-50 text-gray-500 hover:border-gray-300'}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Details & Tags */}
          {step === 3 && (
            <div className="space-y-4">
              {/* Adresse */}
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Adresse</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <input value={adresse} onChange={e => setAdresse(e.target.value)}
                    placeholder="Straße und Hausnummer"
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:bg-white focus:border-[#1B3A5C]/40 focus:outline-none transition-colors" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                  <input value={plz} onChange={e => setPlz(e.target.value)}
                    placeholder="PLZ"
                    className="col-span-1 px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:bg-white focus:border-[#1B3A5C]/40 focus:outline-none transition-colors" />
                  <input value={ort} onChange={e => setOrt(e.target.value)}
                    placeholder="Ort"
                    className="col-span-2 px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:bg-white focus:border-[#1B3A5C]/40 focus:outline-none transition-colors" />
                </div>
              </div>

              {/* Webseite + LinkedIn */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Webseite</label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input value={webseite} onChange={e => setWebseite(e.target.value)}
                      placeholder="www..." type="url"
                      className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:bg-white focus:border-[#1B3A5C]/40 focus:outline-none transition-colors" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Geburtstag</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input value={geburtstag} onChange={e => setGeburtstag(e.target.value)}
                      type="date"
                      className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:bg-white focus:border-[#1B3A5C]/40 focus:outline-none transition-colors" />
                  </div>
                </div>
              </div>

              {/* Kategorie-Labels */}
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Kategorie-Labels</label>
                <LabelPicker selectedLabels={selectedLabels} onChange={setSelectedLabels} />
              </div>

              {/* Betriebs-Zuordnung */}
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Betriebs-Zuordnung</label>
                <BetriebePicker
                  selectedBetriebe={selectedBetriebe}
                  primaererBetrieb={primaererBetrieb}
                  onBetriebeChange={setSelectedBetriebe}
                  onPrimaererBetriebeChange={setPrimaererBetrieb}
                />
              </div>

              {/* Tags */}
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Tags</label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {TAGS_VORSCHLAEGE.map(tag => (
                    <button key={tag} onClick={() => toggleTag(tag)}
                      className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all
                        ${tags.includes(tag)
                          ? 'bg-[#1B3A5C] text-white'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                      {tag}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input value={tagInput} onChange={e => setTagInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addCustomTag()}
                    placeholder="Eigenes Tag..."
                    className="flex-1 px-3 py-2 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:bg-white focus:border-[#1B3A5C]/40 focus:outline-none transition-colors" />
                  <button onClick={addCustomTag}
                    className="px-3 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Notizen */}
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Notizen</label>
                <textarea value={notizen} onChange={e => setNotizen(e.target.value)}
                  placeholder="Interne Notizen zum Kontakt..."
                  rows={3}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:bg-white focus:border-[#1B3A5C]/40 focus:outline-none transition-colors resize-none" />
              </div>
            </div>
          )}
        </div>

        {/* Footer — feste Navigation */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100 flex-shrink-0 bg-white sm:rounded-b-2xl">
          {step > 1 ? (
            <button onClick={() => setStep(s => s - 1)}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors">
              <ChevronLeft className="w-4 h-4" />
              Zurück
            </button>
          ) : (
            <button onClick={onClose}
              className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-500 hover:bg-gray-100 transition-colors">
              Abbrechen
            </button>
          )}

          {step < totalSteps ? (
            <button onClick={() => {
              if (step === 1 && !name.trim()) {
                toast.error('Bitte einen Namen eingeben');
                return;
              }
              setStep(s => s + 1);
            }}
              className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#1B3A5C] hover:bg-[#152d49] transition-colors">
              Weiter
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#C41E3A] hover:bg-[#a8182f] transition-colors disabled:opacity-50">
              {saving ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              Kontakt erstellen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
